import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, Platform } from 'react-native';
import { Modal, Portal, Text, TextInput, Button, Card, IconButton } from 'react-native-paper';
import { colors, spacing, typography } from '../constants/theme';
import { isDetoxTestControlsEnabled } from '../config/environments';
import HandwritingInput from './HandwritingInput';

interface GeneralNotesModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSave: (notes: string) => void;
  initialNotes?: string;
  loading?: boolean;
}

export const GeneralNotesModal: React.FC<GeneralNotesModalProps> = ({
  visible,
  onDismiss,
  onSave,
  initialNotes = '',
  loading = false,
}) => {
  const [notes, setNotes] = useState(initialNotes);
  const [showHandwriting, setShowHandwriting] = useState(false);

  useEffect(() => {
    setNotes(initialNotes);
  }, [initialNotes, visible]);

  const handleSave = () => {
    onSave(notes);
    onDismiss();
  };

  const handleCancel = () => {
    setNotes(initialNotes);
    onDismiss();
  };

  const handleHandwritingComplete = (handwrittenText: string) => {
    // Append handwritten text with a newline if there's existing content
    const updatedNotes = notes ? `${notes}\n${handwrittenText}` : handwrittenText;
    setNotes(updatedNotes);
    setShowHandwriting(false);
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={handleCancel}
        contentContainerStyle={styles.container}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.keyboardView}
        >
          <Card testID="general-notes-modal" style={styles.card}>
            <Card.Title
              title="General Review Notes"
              right={() => (
                isDetoxTestControlsEnabled ? (
                  <Button
                    testID="general-notes-save-button-top"
                    mode="contained"
                    compact
                    onPress={handleSave}
                    loading={loading}
                    disabled={loading}
                    style={styles.detoxSaveButton}
                  >
                    Save
                  </Button>
                ) : null
              )}
            />
            <Card.Content>
              <Text style={styles.description}>
                Add any general notes about this review. These notes will be included in the PDF report.
              </Text>
              <ScrollView style={styles.scrollView}>
                <TextInput
                  testID="general-notes-input"
                  mode="outlined"
                  multiline
                  numberOfLines={10}
                  value={notes}
                  onChangeText={setNotes}
                  placeholder="Enter your general notes here..."
                  style={styles.textInput}
                  disabled={loading}
                />
              </ScrollView>
              {Platform.OS !== 'web' && (
                <Button
                  mode="outlined"
                  onPress={() => setShowHandwriting(true)}
                  icon="draw"
                  style={styles.stylusButton}
                  disabled={loading}
                >
                  Add Notes with Stylus
                </Button>
              )}
            </Card.Content>
            <Card.Actions style={styles.actions}>
              <Button
                testID="general-notes-cancel-button"
                mode="text"
                onPress={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                testID="general-notes-save-button"
                mode="contained"
                onPress={handleSave}
                loading={loading}
                disabled={loading}
              >
                Save Notes
              </Button>
            </Card.Actions>
          </Card>
        </KeyboardAvoidingView>
      </Modal>
      {showHandwriting && (
        <HandwritingInput
          visible={showHandwriting}
          onClose={() => setShowHandwriting(false)}
          onTextConverted={handleHandwritingComplete}
          placeholder="Write additional notes here..."
        />
      )}
    </Portal>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    margin: 20,
    borderRadius: 8,
    maxHeight: '80%',
    minHeight: 400,
  },
  keyboardView: {
    flex: 1,
    minHeight: 400,
  },
  card: {
    backgroundColor: colors.surface,
    flex: 1,
  },
  description: {
    ...typography.body2,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  scrollView: {
    maxHeight: 300,
  },
  textInput: {
    backgroundColor: colors.surface,
    minHeight: 200,
  },
  actions: {
    justifyContent: 'flex-end',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
  },
  stylusButton: {
    marginTop: spacing.sm,
    borderColor: colors.primary,
  },
  detoxSaveButton: {
    marginRight: spacing.sm,
  },
});
